import { EPropertyType } from '@modules/project-creator/shared/enums/property-type.enum';
import { ContactType } from '@modules/contacts/shared/enums/contact-type.enum';
import { EProjectScope, IConfiguratorAnswers } from '../models/configurator.model';
import { EXTERIOR_WORK_NONE, HOUSE_PROPERTY_TYPES } from './configurator-options.data';
import { ERoom } from './configurator-rooms.enum';

export { ERoom } from './configurator-rooms.enum';

/** Shared conditions of the step config, so the rules stay in one place. */

export const isAnswer =
  (field: keyof IConfiguratorAnswers, ...values: string[]) =>
  (answers: IConfiguratorAnswers): boolean =>
    values.includes(answers[field] as string);

export const hasAnswer =
  (field: keyof IConfiguratorAnswers) =>
  (answers: IConfiguratorAnswers): boolean =>
    !!answers[field];

export const isScope =
  (...scopes: EProjectScope[]) =>
  (answers: IConfiguratorAnswers): boolean =>
    !!answers.projectScope && scopes.includes(answers.projectScope);

export const isNotScope =
  (...scopes: EProjectScope[]) =>
  (answers: IConfiguratorAnswers): boolean =>
    !answers.projectScope || !scopes.includes(answers.projectScope);

/**
 * Kind of building, from the project scope where it is implied, otherwise from the chosen property type.
 * Undefined while it is not known (e.g. "whole house" without a house type).
 */
export const getPropertyKind = (answers: IConfiguratorAnswers): string | undefined => {
  switch (answers.projectScope) {
    case EProjectScope.FLAT:
      return EPropertyType.flat;
    case EProjectScope.MULTI_FAMILY:
      return EPropertyType.townhouse;
    case EProjectScope.COMMERCIAL:
      return EPropertyType.commercial;
    case EProjectScope.HOUSE:
      return HOUSE_PROPERTY_TYPES.includes(answers.propertyType) ? answers.propertyType : undefined;
    default:
      return answers.propertyType;
  }
};

export const isPropertyKind =
  (...kinds: string[]) =>
  (answers: IConfiguratorAnswers): boolean =>
    kinds.includes(getPropertyKind(answers));

// Step 3 "Umfang"

export const BATHROOMS_KEY = 'bathrooms';
export const BATHROOMS_MAX = 4;

const getRoomCount = (answers: IConfiguratorAnswers, room: ERoom): number => answers.rooms?.[room] || 0;

/** Flat or house: the rooms counter decides which room sections follow. */
export const isLivingRenovation = isScope(EProjectScope.FLAT, EProjectScope.HOUSE);

/** Whole flat, house, building or commercial unit. */
export const isCompleteRenovation = isScope(
  EProjectScope.FLAT,
  EProjectScope.HOUSE,
  EProjectScope.MULTI_FAMILY,
  EProjectScope.COMMERCIAL
);

export const hasTrade =
  (...trades: string[]) =>
  (answers: IConfiguratorAnswers): boolean =>
    trades.some((trade) => answers.trades?.includes(trade));

/** Number of bathroom sections: one for a bathroom project, otherwise the counted bathrooms (max 4). */
export const getBathroomCount = (answers: IConfiguratorAnswers): number => {
  if (isScope(EProjectScope.BATHROOM, EProjectScope.MULTI_FAMILY)(answers)) return 1;
  if (isScope(EProjectScope.SINGLE_WORKS)(answers) && hasTrade('plumbing', 'tiling')(answers)) return 1;
  if (isLivingRenovation(answers)) return Math.min(getRoomCount(answers, ERoom.BATHROOM), BATHROOMS_MAX);
  return 0;
};

/** More bathrooms counted than sections shown: the last section stands for the rest. */
export const hasMoreBathroomsThanSections = (answers: IConfiguratorAnswers): boolean =>
  isLivingRenovation(answers) && getRoomCount(answers, ERoom.BATHROOM) > BATHROOMS_MAX;

export const getGuestWcCount = (answers: IConfiguratorAnswers): number =>
  isLivingRenovation(answers) ? getRoomCount(answers, ERoom.GUEST_WC) : 0;

export const hasBathroomWork = (answers: IConfiguratorAnswers): boolean =>
  getBathroomCount(answers) > 0 || getGuestWcCount(answers) > 0;

export const hasKitchen = (answers: IConfiguratorAnswers): boolean =>
  isScope(EProjectScope.KITCHEN)(answers) ||
  (isLivingRenovation(answers) && getRoomCount(answers, ERoom.KITCHEN) > 0);

/** Exterior works are planned: an exterior project, a house with more than "nothing outside", or a building
 * whose facade or roof is part of the common areas. */
export const hasExteriorWork = (answers: IConfiguratorAnswers): boolean =>
  isScope(EProjectScope.EXTERIOR)(answers) ||
  (isScope(EProjectScope.HOUSE)(answers) &&
    (answers.exteriorWorks || []).some((work) => work !== EXTERIOR_WORK_NONE)) ||
  (isScope(EProjectScope.MULTI_FAMILY)(answers) &&
    (answers.commonAreas || []).some((area) => ['facade', 'roof'].includes(area)));

// Step 7 "Kontakt"

/** The property address has a postal code and is not marked as unknown. */
export const hasKnownPropertyAddress = (answers: IConfiguratorAnswers): boolean =>
  !!answers.postalCode && !answers.adressUnknown;

export const isCompanyCustomer = (answers: IConfiguratorAnswers): boolean =>
  answers.type === ContactType.COMPANY;
